import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";
import { financeWriteErrorMessage } from "@/components/shared/finance/payment-write-error";
import { useAuth } from "@/features/auth/hooks/use-auth";
import {
  type FinanceCommandKind,
  recoverFinanceCommand,
} from "@/features/finance/lib/retry-safe-command";

const KINDS: FinanceCommandKind[] = [
  "customer_payment",
  "estimate_payment",
  "vendor_payment",
  "customer_credit_application",
];

/** Restores saved finance results after sign-in, even when a paid page hides its action. */
export function FinanceCommandRecovery() {
  const { isApproved, user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  useEffect(() => {
    if (!isApproved || !userId) return;
    let active = true;
    void Promise.allSettled(KINDS.map((kind) => recoverFinanceCommand(kind)))
      .then(async (settled) => {
        if (!active) return;
        const completed = settled.flatMap((item) =>
          item.status === "fulfilled" && item.value ? [item.value] : [],
        );
        if (completed.length > 0) {
          await queryClient.invalidateQueries();
          if (!active) return;
          for (const command of completed) command.acknowledge();
          toast.success("Previous payment activity completed. Balances have been refreshed.");
        }
        if (settled.some((item) => item.status === "rejected")) {
          toast.error("Some previous payments could not be checked. Retry after reconnecting.");
        }
      })
      .catch((error: unknown) => {
        if (active) {
          toast.error(
            financeWriteErrorMessage(
              "finance command recovery",
              error,
              "Could not check previous payments. Try again after reconnecting; your saved attempt is still available.",
            ),
          );
        }
      });
    return () => {
      active = false;
    };
  }, [isApproved, queryClient, userId]);

  return null;
}
